import { Button, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";
import Loader from "../react-lib/generic-ui/Loader";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import { addDoc, collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { db, storage } from "firebaseApp";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useDispatch } from "react-redux";
import { audioPlayerActions } from "store/audioPlayer";


export default function PdfSelector({
  onCreated = (audioTextFileId) => console.log(audioTextFileId),
}) {

  const dispatch = useDispatch();
  
  const auth = useAuth();
  const uid = auth.currentUser?.uid;

  const [file, setFile] = useState(null);

  const [processing, setProcessing] = useState();
  const [message, setMesssage] = useState("Loading");

  const uploadFirstPagesAndCreateDocument = async (pdfDoc, numPages = 1) => {
    // Create document
    const totalPages = pdfDoc.getPageCount();
    const audioTextFile = await addDoc(collection(db, "audioTextFiles"), {
      uid,
      fileName: file.name,
      type: "pdf",
      totalPages,
      createdAt: new Date().getTime(),
    });
    // Process first few pages
    const firstPages = [];
    // We do + 1 so that each page also have the pdf link to the next page
    for (let page = 0; page < Math.min(numPages + 1, totalPages); page++) {
      setMesssage(`Processing page ${page}`)
      const newPdf = await PDFDocument.create();
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [page]);
      newPdf.addPage(copiedPage);
      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const storageRef = ref(
        storage,
        `audioTextFiles/${uid}/${audioTextFile.id}/page-${page}.pdf`
      );
      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);
      firstPages.push({
        uid,
        page,
        totalPages,
        pageUrl: downloadURL,
        createdAt: Date.now(),
        generatingAudio: true,
        audioTextFileId: audioTextFile.id,
        nextPageUrl: null,
        prevPageUrl: null,
      });
      if(page > 0) {
        // Set the link to the next page to avoid breaking
        // sentences when reading the content
        firstPages[page - 1].nextPageUrl = downloadURL;
      }
      dispatch(audioPlayerActions.initialize(audioTextFile.id));
    }
    // We don't do +1 because we only did extra page to get the url
    for (let page = 0; page < Math.min(numPages, totalPages); page++) {
      await setDoc(
        doc(db, "audioTextFiles", audioTextFile.id, "pages", `page-${page}`), 
        firstPages[page]
      );
    }
    onCreated(audioTextFile.id);
    return audioTextFile.id;
  };

  const uploadFullPdf = async (pdfDoc, audioTextFileId) => {
    setMesssage(`Uploading file`)
    const storageRef = ref(
      storage,
      `audioTextFiles/${uid}/${audioTextFileId}/full.pdf`
    );
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(storageRef);
    await updateDoc(
      doc(db, "audioTextFiles", audioTextFileId),
      {
        fullUrl: downloadURL,
      }
    );
  };

  const processFile = async () => {
    try {
      setProcessing(true);
      const fileBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(fileBuffer);
      const audioTextFileId = await uploadFirstPagesAndCreateDocument(pdfDoc, 2);
      await uploadFullPdf(pdfDoc, audioTextFileId);
    } catch (error) {
      console.log(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    event.target.value = null;
  };

  useEffect(() => {
    if (file) {
      processFile();
    }
  }, [file]);

  return (
    <Stack>
      {processing && <Loader message={message} />}
      <Button
        color="primary"
        component="label"
        variant="outlined"
        disabled={processing || !uid}
      >
        Select PDF File
        <input
          data-testid="text-file-uploader-file-selector-input"
          type="file"
          onChange={handleFileChange}
          hidden
          accept=".pdf" // updated to accept more file types
        />
      </Button>
    </Stack>
  );
}
